import { GemSalesConfig } from "./types";

export interface GemSalesScriptOptions {
  autostart: boolean;
  endpoint?: string;
  surfaceEndpoint?: string;
  mode?: GemSalesConfig["mode"];
  shopId?: string;
  brandName?: string;
}

export function readGemSalesScriptOptions(script: Element | null = document.currentScript): GemSalesScriptOptions {
  const endpoint = script?.getAttribute("data-endpoint")?.trim();
  const surfaceEndpoint = script?.getAttribute("data-surface-endpoint")?.trim();
  const mode = script?.getAttribute("data-mode")?.trim();
  // Per-tenant store id (multi-tenant headless). Sent as `store` so the endpoint
  // resolves the right catalog; without it the SDK falls back to the bundled config's.
  const shopId = script?.getAttribute("data-shop-id")?.trim();
  // The bundled config belongs to ONE pilot customer, brand name and all. Without this every store
  // that embeds the SDK renders "<pilot brand> assistant" over its own storefront, which is what
  // pureritual.gemboss.ai was doing: a store called Pure Ritual showing "Wellness Nest assistant".
  // `data-brand-name` wins; otherwise take the name the page already publishes about itself.
  const brandName = script?.getAttribute("data-brand-name")?.trim() || readSiteName();

  return {
    autostart: Boolean(script) && script?.getAttribute("data-autostart") !== "false",
    ...(endpoint ? { endpoint } : {}),
    ...(surfaceEndpoint ? { surfaceEndpoint } : {}),
    ...(mode === "tracking" || mode === "pilot" ? { mode } : {}),
    ...(shopId ? { shopId } : {}),
    ...(brandName ? { brandName } : {}),
  };
}

/** og:site_name is a standard every storefront we generate already emits, so the correct brand is
 *  sitting on the page. Falling back to it means an embed cannot leak the pilot's brand by omission. */
function readSiteName(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const meta = document.querySelector('meta[property="og:site_name"]')?.getAttribute("content")?.trim();
  return meta || undefined;
}

/**
 * Is this embed a store OTHER than the one whose config is bundled?
 *
 * `shopId` is the reliable signal when the embed supplies one. Two wrinkles it has to survive:
 *
 *  - NO shop id at all. The reported bug shape exactly: the brand name still gets rewritten from
 *    og:site_name, so the header says the right company over the pilot's questions. If the page
 *    publishes a different brand than the bundled config's, that is a foreign store even without an id.
 *  - FORM. The bundled config's id is a bare slug ("wellnessnest") while installs are documented with a
 *    store domain. Comparing those raw would strip the pilot's own triggers on its own site, so match
 *    case-insensitively on the leading label rather than demanding an identical string.
 */
function isForeignStore(config: GemSalesConfig, options: GemSalesScriptOptions): boolean {
  if (options.shopId) return storeKey(options.shopId) !== storeKey(config.shopId);
  // No id — fall back to the brand the page publishes about itself.
  const bundledBrand = (config as { brandName?: string }).brandName;
  return Boolean(options.brandName && bundledBrand && options.brandName.trim().toLowerCase() !== bundledBrand.trim().toLowerCase());
}

/** "WellnessNest.myshopify.com" and "wellnessnest" are the same store. */
function storeKey(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase().split(".")[0] ?? "";
}

export function applyGemSalesScriptOptions(config: GemSalesConfig, options: GemSalesScriptOptions): GemSalesConfig {
  // The brand-name fix above stopped a foreign store rendering "<pilot brand> assistant". It did not
  // stop it rendering the pilot's QUESTIONS. `triggers` is the pilot customer's own entry points —
  // their copy, their quick replies, their flows, keyed to their products:
  //
  //     "Not sure where to start? Find the right Wellness Nest product for your goal."
  //     ["Energy and focus", "Workout recovery", "Shilajit routine", "Women's wellness"]
  //
  // Measured on vitalglow.gemboss.ai: a skincare storefront inviting shoppers to ask about shilajit.
  // Renaming the assistant made the header honest and left the questions someone else's.
  //
  // So a foreign store gets NO bundled triggers. Its entry points come from `/surface/chips`, which is
  // already per-store (proven-converting questions for that shop) and which the client already treats
  // as the upgrade path — the base it was upgrading was simply the wrong store's. No chips is the
  // correct empty state here: an assistant that asks nothing is recoverable, one that asks about
  // another brand's products is not.
  const foreignStore = isForeignStore(config, options);
  return {
    ...config,
    // `products` goes with them. Dropping only the triggers left the pitch one layer down: with no
    // Product JSON-LD on the page — a homepage or collection, which is exactly where the chip anchors —
    // pickProduct falls back to the bundled config's FIRST product, and the generator writes it into
    // the prompt as "Product in view" with an instruction to feature it. The skincare store stopped
    // ASKING about shilajit and carried on SELLING it. A foreign store inherits none of the pilot's
    // content: not its questions, not its catalog. Real PDPs still work — those read JSON-LD from the
    // page itself, which is the store's own.
    ...(foreignStore ? { triggers: [], products: {} } : {}),
    ...(options.endpoint ? { endpoint: options.endpoint } : {}),
    ...(options.surfaceEndpoint ? { surfaceEndpoint: options.surfaceEndpoint } : {}),
    ...(options.mode ? { mode: options.mode } : {}),
    ...(options.shopId ? { shopId: options.shopId } : {}),
    ...(options.brandName ? { brandName: options.brandName } : {}),
  };
}
